import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { VisualizationTextMarkupRenderer } from '@mentimeter/tiptap/visualization-text-markup-renderer';
import { type ScaleBand } from 'd3-scale';
import { useRef } from 'react';
import { TextFitGhost } from '../components/text-fit-ghost/TextFitGhost';
import type { ChoicesStaticOptions } from '../types';
import type { RenderChoiceLabel } from '../utils/types/render-options-by-slide-type/types';
import { getLongestLabel } from '../utils/get-longest-label';

const BARS_VISUALIZATION_LABEL_FONTSIZES = [
  '10px',
  '12px',
  '14px',
  '16px',
  '18px',
  '24px',
  '28px',
  '32px',
  '36px',
  '40px',
];

interface BarsAxisLabelsProps {
  staticOptions: ChoicesStaticOptions[];
  labelAccessor: (d: ChoicesStaticOptions) => string;
  keyAccessor: (d: ChoicesStaticOptions) => string;
  discreteScale: ScaleBand<string>;
  totalWidth: number;
  totalHeight: number;
  marginLeft: number;
  marginTop: number;
  color: string;
  fontFamily: string;
  renderChoiceLabel?: RenderChoiceLabel | undefined;
}

export const BarsAxisLabels = ({
  staticOptions,
  labelAccessor,
  keyAccessor,
  discreteScale,
  totalWidth,
  totalHeight,
  marginLeft,
  marginTop,
  color,
  fontFamily,
  renderChoiceLabel,
}: BarsAxisLabelsProps) => {
  const labelsContainerRef = useRef<HTMLDivElement | null>(null);

  const longestLabelId = getLongestLabel(
    staticOptions,
    labelAccessor,
    keyAccessor,
  );

  return (
    <Box
      data-testid="bars-axis-labels"
      aria-hidden
      ref={labelsContainerRef}
      width={totalWidth}
      height={totalHeight}
      position="relative"
      left={marginLeft}
      extend={() => ({ opacity: 0 })}
    >
      {staticOptions.map((d) => {
        const { id } = d;
        const x = discreteScale(keyAccessor(d));
        const labelValue = labelAccessor(d);
        const isLongestLabel = longestLabelId === keyAccessor(d);

        const label = (
          <VisualizationTextMarkupRenderer
            editorContentProps={{
              style: {
                fontFamily,
                fontSize: 'inherit',
              },
            }}
            content={labelValue}
          />
        );

        const labelOverride = renderChoiceLabel?.({
          node: label,
          data: {
            id,
            label: labelValue,
          },
        });

        return (
          <Box
            key={id}
            width={discreteScale.bandwidth()}
            height={totalHeight - marginTop}
            position="absolute"
            left={x}
            top={marginTop}
            style={{
              wordBreak: 'break-word',
              color,
            }}
          >
            {isLongestLabel && (
              <TextFitGhost
                text={labelValue}
                fontSizes={BARS_VISUALIZATION_LABEL_FONTSIZES}
                width={discreteScale.bandwidth()}
                height={totalHeight - marginTop}
                finalFontSizeContainerRef={labelsContainerRef}
              >
                {(ref) => (
                  <Text ref={ref} aria-hidden="true">
                    {labelValue}
                  </Text>
                )}
              </TextFitGhost>
            )}
            {labelOverride ?? label}
          </Box>
        );
      })}
    </Box>
  );
};
